import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Alert, Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import ConfirmDialog from '../components/ConfirmDialog'
import useCopyToClipboard from '../hooks/useCopyToClipboard'
import { useTagList } from '../graphql/tagQueries'
import { highlightsApi } from '../api/highlightsApi'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

const HighlightCardDetails = () => {
  const { cardId } = useParams()
  const navigate = useNavigate()

  const [card, setCard] = useState(null)
  const [content, setContent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteContentDialogOpen, setDeleteContentDialogOpen] = useState(false)
  const { copied, copy: handleCopyId } = useCopyToClipboard(cardId)

  const [editingField, setEditingField] = useState(null)
  const [tempValue, setTempValue] = useState('')

  const { data: tagsData } = useTagList()
  const tags = tagsData?.tags || []
  const tagMap = Object.fromEntries(tags.map((t) => [t.id, t.tag]))

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [cardsResult, contentsResult] = await Promise.all([
        highlightsApi.listCards(),
        highlightsApi.listAllContent(),
      ])
      const cards = cardsResult?.data?.cards || []
      const found = cards.find((c) => c.id === cardId)
      setCard(found || null)
      const contents = contentsResult?.data?.content || []
      setContent(contents.find((c) => c.card_id === cardId) || null)
    } catch (err) {
      setError('Failed to load data. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [cardId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const startEdit = useCallback((field, value) => {
    setEditingField(field)
    setTempValue(value || '')
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingField(null)
    setTempValue('')
  }, [])

  const saveField = useCallback(
    async (field, value) => {
      setError(null)
      try {
        await highlightsApi.updateCard(cardId, { [field]: value })
        setEditingField(null)
        setTempValue('')
        loadData()
      } catch (err) {
        setError('Failed to update card. Please try again.')
      }
    },
    [cardId, loadData],
  )

  const handleDeleteCard = useCallback(async () => {
    setDeleting(true)
    try {
      await highlightsApi.deleteCard(cardId)
      navigate(ROUTE_METADATA.adminOutreachHighlightCards.path)
    } catch (err) {
      setError('Failed to delete card. Please try again.')
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }, [cardId, navigate])

  const handleDeleteContent = useCallback(async () => {
    try {
      await highlightsApi.deleteContent(cardId)
      setDeleteContentDialogOpen(false)
      loadData()
    } catch (err) {
      setError('Failed to delete content. Please try again.')
    }
  }, [cardId, loadData])

  if (loading) return <Loading />

  if (!card) {
    return <Alert severity="error">Highlight card not found</Alert>
  }

  return (
    <>
      <AdminPageHeader>
        <Typography variant="h5" component="h1">
          Highlight Card
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            type="secondary"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate(ROUTE_METADATA.adminOutreachHighlightCards.path)
            }
          >
            Back to Cards
          </Button>
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            sx={{ '& .MuiList-root': { py: 1 } }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                setDeleteDialogOpen(true)
              }}
              sx={{ px: 3, py: 1.5, minWidth: 250, color: 'error.main' }}
            >
              Delete Card
            </MenuItem>
          </Menu>
        </Box>
      </AdminPageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Card preview + details */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Card preview */}
        <Card
          elevation={1}
          sx={{
            borderRadius: '16px !important',
            overflow: 'hidden',
            width: 300,
            flexShrink: 0,
          }}
        >
          {card.media_url ? (
            <CardMedia
              component="img"
              image={card.media_url}
              alt={card.title}
              sx={{ height: 100, objectFit: 'cover' }}
            />
          ) : (
            <Box
              sx={{
                height: 100,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No image
              </Typography>
            </Box>
          )}
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
              }}
            >
              {card.title}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', lineHeight: 1.5 }}
            >
              {card.subtitle || 'No subtitle'}
            </Typography>
          </Box>
        </Card>

        {/* Card details */}
        <Card elevation={0} sx={{ flex: 1 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Card Details
            </Typography>

            <Typography variant="caption" color="text.secondary">
              ID
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 1.5,
                '& .copy-id-icon': { opacity: 0 },
                '&:hover .copy-id-icon': { opacity: 1 },
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {cardId}
              </Typography>
              <IconButton
                className="copy-id-icon"
                size="small"
                onClick={handleCopyId}
                sx={{ p: 0, transition: 'opacity 0.15s' }}
                title="Copy card ID"
                aria-label="Copy card ID"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              {copied && (
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{ ml: 0.5 }}
                >
                  Copied!
                </Typography>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary">
              Tag
            </Typography>
            {editingField === 'tag_id' ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1.5,
                }}
              >
                <Autocomplete
                  disableClearable
                  value={tags.find((t) => t.id === tempValue) || null}
                  onChange={(e, newValue) => {
                    if (newValue) setTempValue(newValue.id)
                  }}
                  options={tags}
                  getOptionLabel={(t) => `${t.tag} (${t.id})`}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      size="small"
                      autoFocus
                      placeholder="Select tag"
                    />
                  )}
                  sx={{ minWidth: 300 }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => saveField('tag_id', tempValue)}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" onClick={cancelEdit}>
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1.5,
                  '& .tag-open-icon': { opacity: 0 },
                  '&:hover .tag-open-icon': { opacity: 1 },
                }}
              >
                <Typography
                  variant="body2"
                  onClick={() => startEdit('tag_id', card.tag_id)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  {tagMap[card.tag_id] || card.tag_id}
                </Typography>
                <IconButton
                  className="tag-open-icon"
                  size="small"
                  onClick={() =>
                    navigate(
                      `${ROUTE_METADATA.adminOutreachTags.path}/${card.tag_id}`,
                    )
                  }
                  sx={{ p: 0, transition: 'opacity 0.15s' }}
                  title="Open tag details"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">
              Title
            </Typography>
            {editingField === 'title' ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1.5,
                }}
              >
                <MuiTextField
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  size="small"
                  autoFocus
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveField('title', tempValue)
                    if (e.key === 'Escape') cancelEdit()
                  }}
                  sx={{ maxWidth: 400 }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => saveField('title', tempValue)}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" onClick={cancelEdit}>
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <Typography
                variant="body2"
                onClick={() => startEdit('title', card.title)}
                sx={{
                  mb: 1.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.7 },
                }}
              >
                {card.title}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              Subtitle
            </Typography>
            {editingField === 'subtitle' ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  mb: 1.5,
                }}
              >
                <MuiTextField
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  size="small"
                  autoFocus
                  fullWidth
                  multiline
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey)
                      saveField('subtitle', tempValue)
                    if (e.key === 'Escape') cancelEdit()
                  }}
                  sx={{ maxWidth: 500 }}
                />
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => saveField('subtitle', tempValue)}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton size="small" onClick={cancelEdit}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <Typography
                variant="body2"
                onClick={() => startEdit('subtitle', card.subtitle)}
                sx={{
                  mb: 1.5,
                  cursor: 'pointer',
                  fontStyle: card.subtitle ? 'normal' : 'italic',
                  '&:hover': { opacity: 0.7 },
                }}
              >
                {card.subtitle || 'Add a subtitle...'}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              Media URL
            </Typography>
            {editingField === 'media_url' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MuiTextField
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  size="small"
                  autoFocus
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveField('media_url', tempValue)
                    if (e.key === 'Escape') cancelEdit()
                  }}
                  sx={{ maxWidth: 500 }}
                  placeholder="Image or video URL"
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => saveField('media_url', tempValue)}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" onClick={cancelEdit}>
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <Typography
                variant="body2"
                onClick={() => startEdit('media_url', card.media_url)}
                sx={{
                  wordBreak: 'break-all',
                  cursor: 'pointer',
                  fontStyle: card.media_url ? 'normal' : 'italic',
                  '&:hover': { opacity: 0.7 },
                }}
              >
                {card.media_url || 'Add a media URL...'}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Card content section */}
      <Card elevation={0}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">Card Content</Typography>
            {content && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  type="secondary"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() =>
                    navigate(
                      `/admin/outreach/highlight-cards/${cardId}/content`,
                    )
                  }
                >
                  Edit Content
                </Button>
                <Button
                  type="secondary"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteContentDialogOpen(true)}
                  sx={{ color: 'error.main' }}
                >
                  Delete Content
                </Button>
              </Box>
            )}
          </Box>

          {content ? (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: 120 }}>
                      Body
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                        }}
                      >
                        {content.body || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Media URL</TableCell>
                    <TableCell>{content.media_url || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Buttons</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: 12,
                        }}
                      >
                        {JSON.stringify(content.buttons || [], null, 2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No content has been created for this card yet.
              </Typography>
              <Button
                type="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(
                    `${ROUTE_METADATA.adminOutreachHighlightCards.path}/${cardId}/content`,
                  )
                }
              >
                Add Content
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteCard}
        title="Delete Card"
        message="Are you sure? This will permanently delete this highlight card and its content."
        confirmLabel="Delete"
        loading={deleting}
      />

      <ConfirmDialog
        open={deleteContentDialogOpen}
        onClose={() => setDeleteContentDialogOpen(false)}
        onConfirm={handleDeleteContent}
        title="Delete Content"
        message="Are you sure? This will delete the content for this card."
        confirmLabel="Delete"
      />
    </>
  )
}

export default HighlightCardDetails
